import * as React from "react";

import { moment } from "../../../ExternalHacks";

import {
  GenericFormConsumerInterface,
  GenericReactProviderFormInterfaceOutput,
  useForm,
  useProminentError,
} from "../../../Common";

import {
  ReactGiftCardInitialStateInterface,
  useReactGiftCardInitialState,
} from "../react-gift-card-initial-state-provider";

import { trackTripleWhaleAdded } from "../../../Common/analytics/triplewhale/track-added";

import {
  initialPurchaseForm,
  PurchaseForm,
  PurchaseFormApp,
  PurchaseFormLogic,
} from "./purchase-form";

interface GiftcardPurchaseConsumerInterface
  extends GenericFormConsumerInterface<PurchaseForm> {
  isAddingToBag: boolean;

  purchaseForm: PurchaseForm;

  giftcardAddedToBag: PurchaseForm | null;

  onAddToBag: () => void;
  scrollToError: boolean;
  onHasScrolledToError: () => void;

  scrollBackToTopOfThePage: boolean;
  onHasScrolledBackToTheTopOfThePage: () => void;

  possibleGiftcardAmounts: ReactGiftCardInitialStateInterface["amounts"];

  reset: () => void;
}

const defaultValue = {
  purchaseForm: {},
  giftcardAddedToBag: null,

  scrollToError: false,

  possibleGiftcardAmounts: [],
};

const GiftcardPurchaseContext = React.createContext(
  defaultValue as unknown as GiftcardPurchaseConsumerInterface
);

export const GiftcardPurchaseProvider: React.FunctionComponent = (props) => {
  const { initialState } = useReactGiftCardInitialState();
  const { setProminentError } = useProminentError();

  const appContext = React.useMemo(() => ({}), []);
  const AppContext = React.useCallback(() => appContext, [appContext]);
  const thisForm = useForm(PurchaseFormLogic, AppContext, initialPurchaseForm);
  const {
    form: purchaseForm,
    onFormFieldFocus,
    onFormInputChange,
    onFormInputBlur,
    resetForm,
    validateRightNowAndGiveMeAYesOrNo,
  } = thisForm;

  const [scrollBackToTopOfThePage, setScrollBackToTopOfThePage] =
    React.useState(false);
  const [scrollToError, setScrollToError] = React.useState(false);

  const [isAddingToBag, setIsAddingToBag] = React.useState<boolean>(false);
  const [giftcardAddedToBag, setGiftcardAddedToBag] =
    React.useState<PurchaseForm | null>(null);

  const onAddToBag = () => {
    if (validateRightNowAndGiveMeAYesOrNo()) {
      addToBag();
    } else {
      setProminentError(
        "Some information is missing or invalid. Check the red fields above for details."
      );
      setScrollToError(true);
    }
  };

  const onHasScrolledToError = () => {
    setScrollToError(false);
  };
  const onHasScrolledBackToTheTopOfThePage = () => {
    setScrollBackToTopOfThePage(false);
  };

  const addToBag = async () => {
    try {
      setIsAddingToBag(true);
      setGiftcardAddedToBag(null);

      const magentoGiftCardOriginalForm = document.querySelector(
        "#product_addtocart_form"
      ) as HTMLFormElement;
      const submitUrl = magentoGiftCardOriginalForm?.action;
      const formKey = (
        magentoGiftCardOriginalForm?.querySelector(
          "[name=form_key]"
        ) as HTMLInputElement
      )?.value;

      const headers = new Headers();
      /**
       * If you look at axios, it actually deletes the headers for formdata:
       * [https://github.com/axios/axios/blob/503418718f669fcc674719fd862b355605d7b41f/lib/adapters/xhr.js#L15-L17]
       *
       * So even though the original code specifies this,
       * we let the browser handle the formdata and send it as a multipart/form-data request.
       */
      // headers.append("Content-Type", "multipart/form-data");

      const bodyFormData = new FormData();
      bodyFormData.append("product", initialState.product_id);
      bodyFormData.append("form_key", formKey);
      bodyFormData.append("qty", "1");
      bodyFormData.append("action_url", submitUrl);
      bodyFormData.append("product_page", "1");
      bodyFormData.append("aw_acp", "1");
      if (purchaseForm.giftcardAmount) {
        bodyFormData.append("giftcard_amount", purchaseForm.giftcardAmount);
      }
      if (purchaseForm.from) {
        bodyFormData.append("giftcard_sender_name", purchaseForm.from);
      }
      if (purchaseForm.emailAddress) {
        bodyFormData.append("giftcard_sender_email", purchaseForm.emailAddress);
      }
      if (purchaseForm.to) {
        bodyFormData.append("giftcard_recipient_name", purchaseForm.to);
      }
      if (purchaseForm.emailAddress) {
        bodyFormData.append(
          "giftcard_recipient_email",
          purchaseForm.emailAddress
        );
      }
      if (purchaseForm.personalMessage) {
        bodyFormData.append("giftcard_message", purchaseForm.personalMessage);
      }

      /**
       * Send date:
       * Will set it to 8 o clock on any day other than today.
       * Otherwise send it immediately.
       */

      let isDeliveringToday = false;
      const rms_date_format_future_day_only_template = "YYYY-MM-DD";
      const rms_date_format = "YYYY-MM-DD HH:mm:ss";
      let theDeliverDate_onboardedToMyFormat = moment(
        purchaseForm.deliveryDate
      ).format(rms_date_format);
      const theDeliverDate_a_moment = moment(
        theDeliverDate_onboardedToMyFormat
      );
      const isTheDeliverDateValid = theDeliverDate_a_moment.isValid();
      if (isTheDeliverDateValid) {
        const isTheDeliveryDateToday = moment().isSame(
          theDeliverDate_a_moment,
          "day"
        );
        if (isTheDeliveryDateToday) {
          isDeliveringToday = true;
        }
      }

      let when_should_i_deliver_in_melbourne_time: string;
      if (isDeliveringToday) {
        when_should_i_deliver_in_melbourne_time =
          theDeliverDate_a_moment.format(rms_date_format);
      } else {
        const what_time_should_it_send_it_on_this_generic_day_in_the_future_as_far_as_i_know =
          "08:00:00";

        const theDayInTheFuture = theDeliverDate_a_moment
          .format(rms_date_format)
          .substring(0, rms_date_format_future_day_only_template.length);
        const theTimeInTheFuture =
          what_time_should_it_send_it_on_this_generic_day_in_the_future_as_far_as_i_know;
        when_should_i_deliver_in_melbourne_time = `${theDayInTheFuture} ${theTimeInTheFuture}`;
      }

      const whenToDeliverInUtc = moment(when_should_i_deliver_in_melbourne_time)
        .utc()
        .format(rms_date_format);
      bodyFormData.append("giftcard_send_date", whenToDeliverInUtc);

      const addUrl = initialState.add_url;

      const request = new Request(addUrl, {
        method: "POST",
        headers,
        body: bodyFormData,
      });

      const initialSubmitResponse = await fetch(request);

      const initialSubmitResponseJson = { data: { backUrl: undefined } };
      initialSubmitResponseJson.data = await initialSubmitResponse.json();

      const finalAddToBagRequestUrl = initialSubmitResponseJson.data.backUrl;
      if (!finalAddToBagRequestUrl) {
        throw new Error("No backUrl in response");
      }

      const finalAddToBagRequest = new Request(finalAddToBagRequestUrl, {
        method: "POST",
        body: bodyFormData,
        headers,
      });

      const finalAddToBagResponse = await fetch(finalAddToBagRequest);

      // Triple Whale AddToCart: this add bypasses the module's ajax binding (fetch,
      // not jQuery), so the successful line is reported to it. Only a confirmed add
      // (`addSuccess` from the Ajaxcartpro response) is reported; analytics never
      // changes the outcome of the purchase flow.
      let finalAddSucceeded = false;
      try {
        const finalAddToBagJson = await finalAddToBagResponse.clone().json();
        finalAddSucceeded =
          !!finalAddToBagJson &&
          (finalAddToBagJson.addSuccess === true ||
            finalAddToBagJson.success === true);
      } catch (parseError) {
        finalAddSucceeded = false;
      }
      if (finalAddSucceeded) {
        void trackTripleWhaleAdded([
          { product_id: initialState.product_id, qty: 1 },
        ]);
      }

      setGiftcardAddedToBag({
        ...purchaseForm,
      });

      setIsAddingToBag(false);
    } catch (error) {
      console.error(error);
      setIsAddingToBag(false);

      let errorMessage = "An unknown error has occurred.";
      if (error instanceof Error && error.message) {
        errorMessage = error.message;
      }

      setProminentError(errorMessage);
    }
  };

  const reset = () => {
    resetForm();
    setGiftcardAddedToBag(null);
    setScrollBackToTopOfThePage(true);
  };

  return (
    <GiftcardPurchaseContext.Provider
      value={{
        ...GenericReactProviderFormInterfaceOutput<
          PurchaseForm,
          PurchaseFormApp
        >(thisForm),

        isAddingToBag,

        purchaseForm,

        giftcardAddedToBag,

        onAddToBag,
        scrollToError,
        onHasScrolledToError,

        scrollBackToTopOfThePage,
        onHasScrolledBackToTheTopOfThePage,

        possibleGiftcardAmounts: initialState.amounts,

        reset,
      }}
    >
      {props?.children}
    </GiftcardPurchaseContext.Provider>
  );
};

export const useGiftcardPurchase = (): GiftcardPurchaseConsumerInterface =>
  React.useContext(GiftcardPurchaseContext);
